import { useState, useEffect } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ChatCircle,
  Plus,
  Trash,
  PencilSimple,
  Brain,
  Bell,
  Gear,
  X,
  CompassRose,
  MagnifyingGlass,
  UsersThree,
  AddressBook,
} from "@phosphor-icons/react";
import { useApp } from "../context/AppContext";
import * as API from "../lib/api";
import { Button } from "./ui/button";
import ItemMenu from "./ItemMenu";
import DraftDot from "./DraftDot";
import JourneyAvatar from "./JourneyAvatar";
import { toast } from "sonner";

export default function Sidebar({ open, onClose }) {
  const { conversations, refreshConversations, activeConvId, setActiveConvId } =
    useApp();
  const navigate = useNavigate();
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState("");

  const newChat = async () => {
    setActiveConvId(null);
    navigate("/app");
    onClose?.();
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this conversation?")) return;
    try {
      await API.deleteConversation(id);
      await refreshConversations();
      if (activeConvId === id) {
        setActiveConvId(null);
        navigate("/app");
      }
      toast.success("Conversation deleted");
    } catch {
      toast.error("Failed to delete");
    }
  };

  const startRename = (c) => {
    setEditingId(c.id);
    setEditValue(c.title);
  };

  const submitRename = async (id) => {
    try {
      if (editValue.trim()) {
        await API.renameConversation(id, editValue.trim());
        await refreshConversations();
      }
    } catch {
      toast.error("Rename failed");
    } finally {
      setEditingId(null);
      setEditValue("");
    }
  };

  const navItems = [
    { to: "/app/search", label: "Search", icon: MagnifyingGlass },
    { to: "/app/memory", label: "Memory", icon: Brain },
    {
      to: "/app/reminders",
      label: "Reminders",
      icon: Bell,
      draftMatch: "reminder-new",
    },
    {
      to: "/app/tasks",
      label: "Tasks",
      icon: UsersThree,
      draftMatch: /^task-(new|compose:)/,
    },
    { to: "/app/contacts", label: "Contacts", icon: AddressBook },
    { to: "/app/settings", label: "Settings", icon: Gear },
  ];

  return (
    <>
      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="sidebar-backdrop"
            className="fixed inset-0 bg-black/60 z-40 lg:hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />
        )}
      </AnimatePresence>

      <motion.aside
        data-testid="sidebar"
        initial={false}
        animate={{ x: open ? 0 : "-100%" }}
        transition={{ type: "tween", duration: 0.25 }}
        className="fixed lg:static inset-y-0 left-0 z-50 w-[82%] max-w-[320px] lg:w-72 lg:max-w-none lg:translate-x-0 bg-[#070707] border-r border-white/5 flex flex-col safe-top safe-bottom"
        style={{ transform: open ? undefined : undefined }}
      >
        <div className="flex items-center justify-between px-5 py-5 border-b border-white/5">
          <Link
            to="/app"
            onClick={onClose}
            className="flex items-center gap-2.5"
            data-testid="sidebar-logo"
          >
            <JourneyAvatar size={36} testId="sidebar-avatar" />
            <span className="font-display text-lg font-semibold tracking-tight">
              Journey
            </span>
          </Link>
          <button
            onClick={onClose}
            className="lg:hidden p-3 -m-1 text-white/60 hover:text-white"
            data-testid="sidebar-close"
            aria-label="Close sidebar"
          >
            <X size={20} />
          </button>
        </div>

        <div className="px-3 pt-3">
          <Button
            onClick={newChat}
            data-testid="sidebar-new-chat"
            className="w-full justify-start gap-2 bg-cyan-500 hover:bg-cyan-400 text-black font-medium rounded-xl h-11"
          >
            <Plus size={18} weight="bold" />
            New chat
          </Button>
        </div>

        <div className="flex-1 overflow-y-auto px-2 pt-4">
          <div className="px-3 mb-2 text-[10px] uppercase tracking-[0.18em] text-white/40 font-medium">
            Conversations
          </div>
          <ul className="space-y-0.5">
            {conversations.length === 0 && (
              <li className="px-3 py-3 text-sm text-white/40">
                No conversations yet
              </li>
            )}
            {conversations.map((c) => (
              <li key={c.id}>
                <div
                  onClick={() => {
                    setActiveConvId(c.id);
                    navigate(`/app?c=${c.id}`);
                    onClose?.();
                  }}
                  data-testid={`conversation-item-${c.id}`}
                  className={`group flex items-center gap-2 px-3 py-2.5 rounded-lg cursor-pointer transition-all ${
                    activeConvId === c.id
                      ? "bg-white/5 text-white"
                      : "text-white/70 hover:bg-white/5 hover:text-white"
                  }`}
                >
                  <ChatCircle size={16} weight="duotone" className="shrink-0 text-cyan-400/80" />
                  {editingId === c.id ? (
                    <input
                      autoFocus
                      className="flex-1 bg-transparent text-sm outline-none border-b border-cyan-500/40 pb-0.5"
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      onBlur={() => submitRename(c.id)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") submitRename(c.id);
                        if (e.key === "Escape") {
                          setEditingId(null);
                          setEditValue("");
                        }
                      }}
                      data-testid={`conversation-rename-input-${c.id}`}
                    />
                  ) : (
                    <span className="flex-1 truncate text-sm">{c.title}</span>
                  )}
                  <DraftDot
                    keyName={`chat:${c.id}`}
                    testId={`conversation-draft-dot-${c.id}`}
                    className="mr-1"
                  />
                  <div className="opacity-100 transition-opacity flex items-center" onClick={(e) => e.stopPropagation()}>
                    <ItemMenu
                      testId={`conversation-menu-${c.id}`}
                      items={[
                        {
                          label: "Rename",
                          icon: PencilSimple,
                          onClick: () => startRename(c),
                          testId: `conversation-rename-${c.id}`,
                        },
                        { type: "separator" },
                        {
                          label: "Delete",
                          icon: Trash,
                          onClick: () => handleDelete(c.id),
                          destructive: true,
                          testId: `conversation-delete-${c.id}`,
                        },
                      ]}
                    />
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>

        <nav className="border-t border-white/5 p-2">
          {navItems.map(({ to, label, icon: Icon, draftMatch }) => (
            <NavLink
              key={to}
              to={to}
              onClick={onClose}
              data-testid={`nav-${label.toLowerCase()}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                  isActive
                    ? "bg-white/5 text-white"
                    : "text-white/70 hover:bg-white/5 hover:text-white"
                }`
              }
            >
              <Icon size={18} weight="duotone" className="text-cyan-400/80" />
              <span className="flex-1">{label}</span>
              {draftMatch && (
                <DraftDot
                  keyName={typeof draftMatch === "string" ? draftMatch : undefined}
                  match={draftMatch instanceof RegExp ? draftMatch : undefined}
                  testId={`nav-draft-dot-${label.toLowerCase()}`}
                />
              )}
            </NavLink>
          ))}
        </nav>
      </motion.aside>
    </>
  );
}
